import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toggleMenuContent } from "../../store/themeConfigSlice";
import { STRING_MENU } from "../../constants/string";
import { showToast } from "../../components/custom/custom-toast";
import SettingService, { SETTING_KEY, SettingItem } from "./services/setting-service";
import HrmSettingService, { CompanySetting } from "./services/hrm-setting-service";
import WorkScheduleService, {
  AttendanceScheduleAssignment,
  ScheduleDayConfig,
  SCHEDULE_ASSIGNMENT_SCOPE,
  WorkScheduleTemplate,
} from "./services/work-schedule-service";
import { FAULT_TYPE } from "../fault-ticket/services/fault-ticket-service";
import DepartmentService, { Department } from "../department/services/department-service";
import { FaultSlaEditor } from "./components/fault-sla-editor";
import { ItemDebtPenaltyEditor } from "./components/item-debt-penalty-editor";
import {
  ItemDebtPenaltyConfig,
  defaultItemDebtPenaltyConfig,
  parseItemDebtPenaltyConfig,
  serializeItemDebtPenaltyConfig,
} from "./item-debt-penalty-config";
import { getCurrentCoordinates } from "../../utils/geolocation";
import {
  FaultSlaConfig,
  defaultSlaConfig,
  parseSlaConfig,
  serializeSlaConfig,
} from "./fault-sla-config";

// Class dùng chung cho các card.
const CARD = "bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden w-full";
const CARD_HEAD = "px-5 py-4 border-b border-slate-100";
const CARD_TITLE = "text-xl font-semibold text-slate-800";
const ROW = "grid grid-cols-10 gap-4 items-center py-4";
const NUM_INPUT =
  "w-full border rounded-lg px-3 py-2 text-right focus:outline-none focus:ring-1 focus:ring-green-500";

// weekday: 2=T2 ... 7=T7, 8=CN — khớp quy ước ScheduleDayConfig bên hrm.
const OFFICE_WEEKDAYS: { weekday: number; label: string }[] = [
  { weekday: 2, label: "Thứ 2" },
  { weekday: 3, label: "Thứ 3" },
  { weekday: 4, label: "Thứ 4" },
  { weekday: 5, label: "Thứ 5" },
  { weekday: 6, label: "Thứ 6" },
  { weekday: 7, label: "Thứ 7" },
  { weekday: 8, label: "Chủ nhật" },
];

const defaultOfficeDays = (): ScheduleDayConfig[] =>
  OFFICE_WEEKDAYS.map(({ weekday }) => ({
    weekday,
    start_time: "08:00",
    end_time: "17:00",
    is_working: [7,8].includes(weekday) ? 0 : 1, // mặc định nghỉ Chủ nhật
  }));

const SettingPage = () => {
  const dispatch = useDispatch();
  dispatch(toggleMenuContent(STRING_MENU.SETTING));

  const { fetch: fetchAll } = SettingService.getAll();
  const { fetch: updateAll, loading: saving } = SettingService.updateAll();
  const { fetch: fetchDepartments } = DepartmentService.list();
  const { fetch: fetchCompanySetting } = HrmSettingService.getCompanySetting();
  const { fetch: saveCompanySetting, loading: savingHqLocation } = HrmSettingService.updateCompanySetting();
  const { fetch: fetchAssignments } = WorkScheduleService.listAssignments();
  const { fetch: fetchTemplateDetail } = WorkScheduleService.detailTemplate();
  const { fetch: createTemplate, loading: creatingTemplate } = WorkScheduleService.createTemplate();
  const { fetch: updateTemplate, loading: updatingTemplate } = WorkScheduleService.updateTemplate();
  const { fetch: upsertAssignment, loading: upsertingAssignment } = WorkScheduleService.upsertAssignment();

  // --- Cấu hình phiếu lỗi ---
  const [explainHours, setExplainHours] = useState<string>("");
  const [pointToVnd, setPointToVnd] = useState<string>(""); // chuỗi đã format "50.000"
  // Mức phạt theo loại lỗi tự động — nay tính bằng ĐIỂM (số nguyên nhỏ), không phải VND.
  const [penaltyByType, setPenaltyByType] = useState<Record<number, string>>({});
  // --- Nợ đồ ---
  const [itemDebtDueHours, setItemDebtDueHours] = useState<string>("");
  const [itemDebtPenalty, setItemDebtPenalty] = useState<ItemDebtPenaltyConfig>(defaultItemDebtPenaltyConfig());
  const [itemDebtPenaltyReloadKey, setItemDebtPenaltyReloadKey] = useState(0);
  // --- Vị trí hoàn thành giao hàng ---
  const [deliveryLocationMaxM, setDeliveryLocationMaxM] = useState<string>("");
  // --- Mốc phạt SLA ---
  const [slaConfig, setSlaConfig] = useState<FaultSlaConfig>(defaultSlaConfig());
  const [departments, setDepartments] = useState<Department[]>([]);
  // --- Điều phối tài xế ---
  const [dispatchMinutes, setDispatchMinutes] = useState<string>("");
  const [acceptTimeout, setAcceptTimeout] = useState<string>("");
  const [reminderInterval, setReminderInterval] = useState<string>("");

  // Đồng bộ phiếu lỗi sang hệ thống đối tác
  const [partnerSyncEnabled, setPartnerSyncEnabled] = useState(false);
  const [partnerSyncUrl, setPartnerSyncUrl] = useState<string>("");
  const [partnerSyncSecret, setPartnerSyncSecret] = useState<string>("");

  const [loaded, setLoaded] = useState(false);

  // --- Trụ sở chính (tọa độ + bán kính chấm công mặc định) ---
  // Giữ nguyên object gốc để khi lưu, những phần trang này chưa có UI (lịch làm việc, chính sách đi
  // trễ/về sớm, nghỉ trưa...) không bị mất — BE nhận/trả nguyên cụm, không có patch từng phần.
  const [companySetting, setCompanySetting] = useState<CompanySetting | null>(null);
  const [hqLatitude, setHqLatitude] = useState<string>("");
  const [hqLongitude, setHqLongitude] = useState<string>("");
  const [hqRadius, setHqRadius] = useState<string>("");
  const [hqAddress, setHqAddress] = useState<string>("");
  const [isLocatingHq, setIsLocatingHq] = useState(false);
  const [hqLocationError, setHqLocationError] = useState<string | null>(null);

  // --- Giờ hành chính (NV mode FIXED/NO_ATTENDANCE) ---
  // Đây là lịch mặc định toàn hệ thống (scope DEFAULT) — NV chưa được gán lịch riêng theo bộ
  // phận/cá nhân sẽ dùng lịch này. work_branch_id luôn NULL: NV hành chính dùng tọa độ trụ sở chính
  // (card "Trụ sở chính" ở trên), không chọn chi nhánh riêng.
  const [officeDays, setOfficeDays] = useState<ScheduleDayConfig[]>(defaultOfficeDays());
  const [officeTemplateId, setOfficeTemplateId] = useState<number | null>(null);
  const [officeAssignmentId, setOfficeAssignmentId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Danh sách bộ phận (chỉ active) để chọn ở cấu hình SLA.
    fetchDepartments({ status: 1 }).then((res) => {
      if (cancelled) return;
      const data = res?.data as any;
      const list: Department[] = Array.isArray(data) ? data : data?.list ?? [];
      setDepartments(list);
    });

    fetchCompanySetting().then((res) => {
      if (cancelled) return;
      const data = res?.data as CompanySetting | undefined;
      if (!data) return;
      setCompanySetting(data);
      setHqLatitude(data.company_location?.latitude != null ? String(data.company_location.latitude) : "");
      setHqLongitude(data.company_location?.longitude != null ? String(data.company_location.longitude) : "");
      setHqRadius(data.company_location?.radius != null ? String(data.company_location.radius) : "");
      setHqAddress(data.company_location?.address ?? "");
    });

    // Lịch "giờ hành chính" mặc định toàn hệ thống (scope DEFAULT) nếu đã từng cấu hình.
    fetchAssignments({ scope_type: SCHEDULE_ASSIGNMENT_SCOPE.DEFAULT }).then((res) => {
      if (cancelled) return;
      const list = (res?.data as AttendanceScheduleAssignment[] | undefined) ?? [];
      const defaultAssignment = list.find((a) => a.scope_type === SCHEDULE_ASSIGNMENT_SCOPE.DEFAULT);
      if (!defaultAssignment) return; // Chưa cấu hình lần nào — giữ nguyên giá trị mặc định của form.

      setOfficeAssignmentId(defaultAssignment.id);
      fetchTemplateDetail(defaultAssignment.schedule_template_id).then((tRes) => {
        if (cancelled) return;
        const template = tRes?.data as WorkScheduleTemplate | undefined;
        if (!template) return;
        setOfficeTemplateId(template.id);
        if (Array.isArray(template.days_parsed) && template.days_parsed.length === 7) {
          setOfficeDays(template.days_parsed);
        }
      });
    });

    fetchAll().then((res) => {
      if (cancelled) return;
      const list: SettingItem[] = (res?.data as SettingItem[]) ?? [];
      const map = new Map(list.map((s) => [s.key, s.value]));

      setExplainHours(map.get(SETTING_KEY.FAULT_EXPLAIN_HOURS) ?? "");
      setDispatchMinutes(map.get(SETTING_KEY.SUPERVISOR_DISPATCH_MINUTES) ?? "");
      setAcceptTimeout(map.get(SETTING_KEY.ACCEPT_TIMEOUT) ?? "");
      setReminderInterval(map.get(SETTING_KEY.REMINDER_INTERVAL) ?? "");
      setItemDebtDueHours(map.get(SETTING_KEY.ITEM_DEBT_DUE_HOURS) ?? "");
      setItemDebtPenalty(parseItemDebtPenaltyConfig(map.get(SETTING_KEY.ITEM_DEBT_PENALTY)));
      setItemDebtPenaltyReloadKey((k) => k + 1);
      setDeliveryLocationMaxM(map.get(SETTING_KEY.DELIVERY_LOCATION_MAX_METERS) ?? "");
      setPartnerSyncEnabled((map.get(SETTING_KEY.PARTNER_FAULT_SYNC_ENABLED) ?? "") === "1");
      setPartnerSyncUrl(map.get(SETTING_KEY.PARTNER_FAULT_SYNC_URL) ?? "");
      setPartnerSyncSecret(map.get(SETTING_KEY.PARTNER_FAULT_SYNC_SECRET) ?? "");

      const p2v = Number(map.get(SETTING_KEY.POINT_TO_VND));
      setPointToVnd(p2v > 0 ? p2v.toLocaleString("vi-VN") : "");

      setSlaConfig(parseSlaConfig(map.get(SETTING_KEY.FAULT_SLA)));

      // CONFIG_FAULT_PENALTY nay là map fault_type → ĐIỂM.
      const penaltyRaw = map.get(SETTING_KEY.FAULT_PENALTY);
      const next: Record<number, string> = {};
      if (penaltyRaw) {
        try {
          const obj = JSON.parse(penaltyRaw) as Record<string, number>;
          Object.entries(obj).forEach(([type, points]) => {
            const v = Number(points);
            if (v > 0) next[Number(type)] = String(v);
          });
        } catch {
          /* ignore JSON lỗi */
        }
      }
      setPenaltyByType(next);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUseHqCurrentLocation = async () => {
    setIsLocatingHq(true);
    setHqLocationError(null);
    try {
      const { latitude, longitude } = await getCurrentCoordinates();
      setHqLatitude(String(latitude));
      setHqLongitude(String(longitude));
    } catch (error: any) {
      setHqLocationError(error?.message ?? "Không thể lấy vị trí hiện tại.");
    } finally {
      setIsLocatingHq(false);
    }
  };

  const handleSaveHqLocation = async () => {
    if (!companySetting) {
      showToast({ type: "error", message: "Chưa tải được cấu hình hiện tại, vui lòng tải lại trang." });
      return;
    }
    const latitude = Number(hqLatitude);
    const longitude = Number(hqLongitude);
    const radius = Number(hqRadius);
    if (!hqLatitude || Number.isNaN(latitude)) {
      showToast({ type: "error", message: "Vui lòng nhập vĩ độ hợp lệ." });
      return;
    }
    if (!hqLongitude || Number.isNaN(longitude)) {
      showToast({ type: "error", message: "Vui lòng nhập kinh độ hợp lệ." });
      return;
    }
    if (!hqRadius || Number.isNaN(radius) || radius < 0) {
      showToast({ type: "error", message: "Vui lòng nhập bán kính hợp lệ." });
      return;
    }

    // Gửi nguyên object cũ, chỉ thay company_location — các phần khác (lịch làm việc, chính sách đi
    // trễ/về sớm...) trang này chưa có UI nên phải giữ nguyên, không phải BE tự merge giúp.
    const payload: CompanySetting = {
      ...companySetting,
      company_location: { latitude, longitude, radius, address: hqAddress.trim() },
    };

    try {
      const res = await saveCompanySetting(payload);
      if (res && (res.status === 200 || res.status === undefined)) {
        setCompanySetting(payload);
        showToast({ type: "success", message: "Đã lưu tọa độ trụ sở chính" });
      } else {
        showToast({ type: "error", message: res?.message || "Lưu tọa độ trụ sở chính thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Lưu tọa độ trụ sở chính thất bại" });
    }
  };

  const updateOfficeDay = (weekday: number, patch: Partial<ScheduleDayConfig>) => {
    setOfficeDays((prev) => prev.map((d) => (d.weekday === weekday ? { ...d, ...patch } : d)));
  };

  const handleSaveOfficeHours = async () => {
    for (const day of officeDays) {
      if (day.is_working && day.start_time >= day.end_time) {
        const label = OFFICE_WEEKDAYS.find((w) => w.weekday === day.weekday)?.label ?? "";
        showToast({ type: "error", message: `${label}: giờ vào phải trước giờ ra.` });
        return;
      }
    }

    const payload = {
      name: "Giờ hành chính",
      description: "Lịch làm việc mặc định cho NV văn phòng (chế độ chấm công FIXED) — quản lý ở Cài đặt hệ thống.",
      days: officeDays,
    };

    try {
      let templateId = officeTemplateId;
      if (templateId) {
        const res = await updateTemplate(templateId, payload);
        if (!res || (res.status !== 200 && res.status !== undefined)) {
          showToast({ type: "error", message: res?.message || "Lưu giờ hành chính thất bại" });
          return;
        }
      } else {
        const res = await createTemplate(payload);
        if (!res || (res.status !== 200 && res.status !== undefined) || !res.data) {
          showToast({ type: "error", message: res?.message || "Lưu giờ hành chính thất bại" });
          return;
        }
        templateId = (res.data as WorkScheduleTemplate).id;
        setOfficeTemplateId(templateId);
      }

      const assignRes = await upsertAssignment({
        scope_type: SCHEDULE_ASSIGNMENT_SCOPE.DEFAULT,
        schedule_template_id: templateId,
        // NV hành chính dùng tọa độ trụ sở chính (card "Trụ sở chính" ở trên), không chọn chi nhánh riêng.
        work_branch_id: null,
        note: "Giờ hành chính mặc định — áp dụng cho NV chế độ FIXED/NO_ATTENDANCE chưa gán lịch riêng theo bộ phận/cá nhân",
      });
      if (!assignRes || (assignRes.status !== 200 && assignRes.status !== undefined)) {
        showToast({ type: "error", message: assignRes?.message || "Lưu giờ hành chính thất bại" });
        return;
      }
      setOfficeAssignmentId((assignRes.data as AttendanceScheduleAssignment)?.id ?? officeAssignmentId);

      showToast({ type: "success", message: "Đã lưu giờ hành chính" });
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Lưu giờ hành chính thất bại" });
    }
  };

  const savingOfficeHours = creatingTemplate || updatingTemplate || upsertingAssignment;

  // Mức phạt theo điểm — chỉ nhận số nguyên.
  const setPenalty = (type: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    setPenaltyByType((prev) => ({ ...prev, [type]: digits }));
  };

  // Ô nhập mức phạt (điểm) cho 1 loại lỗi tự động — tái dùng ở Card 1 & Card điều phối.
  const penaltyPointRow = (type: number, label: string, desc: string) => (
    <div className={ROW}>
      <div className="col-span-7">
        <div className="text-sm font-medium text-slate-700">Mức phạt: {label} (điểm)</div>
        <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
      </div>
      <div className="col-span-3">
        <input
          type="number"
          min="0"
          value={penaltyByType[type] ?? ""}
          onChange={(e) => setPenalty(type, e.target.value)}
          placeholder="0 (không phạt)"
          className={NUM_INPUT}
        />
      </div>
    </div>
  );

  const handleSave = async () => {
    // JSON mức phạt theo ĐIỂM (chỉ loại có giá trị > 0)
    const penaltyMap: Record<number, number> = {};
    Object.entries(penaltyByType).forEach(([type, val]) => {
      const v = Number(val);
      if (v > 0) penaltyMap[Number(type)] = v;
    });

    const settings: SettingItem[] = [
      { key: SETTING_KEY.FAULT_EXPLAIN_HOURS, value: explainHours ? String(Number(explainHours)) : "0" },
      { key: SETTING_KEY.POINT_TO_VND, value: String(Number((pointToVnd || "").replace(/\D/g, "")) || 0) },
      { key: SETTING_KEY.FAULT_PENALTY, value: JSON.stringify(penaltyMap) },
      { key: SETTING_KEY.FAULT_SLA, value: serializeSlaConfig(slaConfig) },
      { key: SETTING_KEY.SUPERVISOR_DISPATCH_MINUTES, value: dispatchMinutes ? String(Number(dispatchMinutes)) : "30" },
      { key: SETTING_KEY.ACCEPT_TIMEOUT, value: acceptTimeout ? String(Number(acceptTimeout)) : "15" },
      { key: SETTING_KEY.REMINDER_INTERVAL, value: reminderInterval ? String(Number(reminderInterval)) : "1" },
      // Để trống = dùng mặc định 48h của BE; nhập 0 = tắt hẳn phạt nợ đồ quá hạn.
      { key: SETTING_KEY.ITEM_DEBT_DUE_HOURS, value: itemDebtDueHours === "" ? "48" : String(Number(itemDebtDueHours)) },
      { key: SETTING_KEY.ITEM_DEBT_PENALTY, value: serializeItemDebtPenaltyConfig(itemDebtPenalty) },
      // Để trống / 0 = dùng mặc định 1000m của BE.
      {
        key: SETTING_KEY.DELIVERY_LOCATION_MAX_METERS,
        value: Number(deliveryLocationMaxM) > 0 ? String(Math.round(Number(deliveryLocationMaxM))) : "1000",
      },

      { key: SETTING_KEY.PARTNER_FAULT_SYNC_ENABLED, value: partnerSyncEnabled ? "1" : "0" },
      { key: SETTING_KEY.PARTNER_FAULT_SYNC_URL, value: partnerSyncUrl.trim() },
      { key: SETTING_KEY.PARTNER_FAULT_SYNC_SECRET, value: partnerSyncSecret.trim() },
    ];

    try {
      const res = await updateAll(settings);
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã lưu cấu hình" });
      } else {
        showToast({ type: "error", message: res?.message || "Lưu cấu hình thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Lưu cấu hình thất bại" });
    }
  };

  return (
    <div className="p-6 space-y-4 pb-24">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Cài đặt hệ thống</h1>
      </div>

      {/* Card 0: Trụ sở chính — tọa độ + bán kính mặc định để chấm công. Lưu riêng (nút riêng), không
          gộp vào nút "Lưu cấu hình" ở cuối trang vì đây là service/hợp đồng dữ liệu khác (hrm, gửi
          nguyên object thay vì patch từng key như các card còn lại). */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Trụ sở chính</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Tọa độ + bán kính mặc định để tính khoảng cách chấm công. Chi nhánh nào chưa tự cấu hình
            tọa độ riêng (ở màn Chi nhánh) sẽ dùng tọa độ này.
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={handleUseHqCurrentLocation}
              disabled={isLocatingHq}
              className="rounded-lg border border-green-500 px-3 py-2 text-xs font-semibold text-green-600 hover:bg-green-50 disabled:opacity-60 whitespace-nowrap"
            >
              {isLocatingHq ? "Đang định vị..." : "📍 Lấy vị trí hiện tại"}
            </button>
          </div>

          {hqLocationError && <p className="text-xs text-red-500">{hqLocationError}</p>}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Vĩ độ (latitude)</label>
              <input
                type="number"
                step="any"
                value={hqLatitude}
                onChange={(e) => setHqLatitude(e.target.value)}
                placeholder="VD: 10.762622"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Kinh độ (longitude)</label>
              <input
                type="number"
                step="any"
                value={hqLongitude}
                onChange={(e) => setHqLongitude(e.target.value)}
                placeholder="VD: 106.660172"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Bán kính hợp lệ (mét)</label>
              <input
                type="number"
                min={0}
                step={10}
                value={hqRadius}
                onChange={(e) => setHqRadius(e.target.value)}
                placeholder="VD: 500"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Địa chỉ (hiển thị tham khảo)</label>
            <input
              type="text"
              value={hqAddress}
              onChange={(e) => setHqAddress(e.target.value)}
              placeholder="VD: 86 Nguyễn Cửu Vân, Gia Định, Hồ Chí Minh, Vietnam"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="button"
              disabled={!companySetting || savingHqLocation}
              onClick={handleSaveHqLocation}
              className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold shadow-sm hover:bg-green-700 disabled:opacity-60"
            >
              {savingHqLocation ? "Đang lưu..." : "Lưu tọa độ trụ sở chính"}
            </button>
          </div>
        </div>
      </div>

      {/* Card: Giờ hành chính — lịch làm việc mặc định cho NV văn phòng (mode FIXED). Lưu riêng như
          card "Trụ sở chính" ở trên vì cùng đụng tới hrm (khác service/giao thức với các card phía
          dưới). NV hành chính luôn dùng tọa độ trụ sở chính — không chọn chi nhánh ở đây. */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Giờ hành chính</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Lịch làm việc mặc định cho nhân viên văn phòng (chế độ chấm công "Giờ hành chính") — áp
            dụng khi nhân viên/bộ phận chưa được gán lịch riêng. Địa điểm chấm công dùng tọa độ trụ sở
            chính ở card phía trên.
          </p>
        </div>

        <div className="px-5 py-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-3">Thứ</th>
                  <th className="py-2 pr-3">Ngày làm việc</th>
                  <th className="py-2 pr-3">Giờ vào</th>
                  <th className="py-2 pr-3">Giờ ra</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {officeDays.map((day) => {
                  const label = OFFICE_WEEKDAYS.find((w) => w.weekday === day.weekday)?.label ?? "";
                  return (
                    <tr key={day.weekday}>
                      <td className="py-2 pr-3 font-medium text-slate-700">{label}</td>
                      <td className="py-2 pr-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={day.is_working === 1}
                            onChange={(e) => updateOfficeDay(day.weekday, { is_working: e.target.checked ? 1 : 0 })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-green-600 peer-focus:ring-2 peer-focus:ring-green-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white" />
                        </label>
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          type="time"
                          value={day.start_time}
                          disabled={!day.is_working}
                          onChange={(e) => updateOfficeDay(day.weekday, { start_time: e.target.value })}
                          className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          type="time"
                          value={day.end_time}
                          disabled={!day.is_working}
                          onChange={(e) => updateOfficeDay(day.weekday, { end_time: e.target.value })}
                          className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="button"
              disabled={savingOfficeHours}
              onClick={handleSaveOfficeHours}
              className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold shadow-sm hover:bg-green-700 disabled:opacity-60"
            >
              {savingOfficeHours ? "Đang lưu..." : "Lưu giờ hành chính"}
            </button>
          </div>
        </div>
      </div>

      {/* Card 1: Cấu hình phiếu lỗi (giải trình + quy đổi điểm→tiền + phạt Không nộp quỹ) */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Cấu hình phiếu lỗi</h2>
          <p className="text-sm text-slate-500 mt-0.5">Quy tắc chung áp cho mọi phiếu lỗi (tay & tự động).</p>
        </div>

        <div className="px-5 divide-y divide-slate-100">
          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Thời gian cho phép giải trình (giờ)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Quá thời gian này mà chưa xử lý, hệ thống tự chốt "Đã ghi lỗi".
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min="0"
                value={explainHours}
                onChange={(e) => setExplainHours(e.target.value)}
                placeholder="VD: 24"
                className={NUM_INPUT}
              />
            </div>
          </div>

          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Giá trị quy đổi: 1 điểm phạt = ? (VND)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Điểm phạt của phiếu được quy đổi ra tiền theo tỉ lệ này (chốt tại thời điểm tạo phiếu).
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="text"
                inputMode="numeric"
                value={pointToVnd}
                onChange={(e) => {
                  const d = e.target.value.replace(/\D/g, "");
                  setPointToVnd(d ? Number(d).toLocaleString("vi-VN") : "");
                }}
                placeholder="VD: 50.000"
                className={NUM_INPUT}
              />
            </div>
          </div>

          {penaltyPointRow(
            FAULT_TYPE.KHONG_NOP_QUY,
            "Không nộp quỹ",
            "Phạt tài xế còn dư quỹ chưa nộp tính đến 16h30 hằng ngày.",
          )}

          {penaltyPointRow(
            FAULT_TYPE.THIEU_DO,
            "Thất thoát đồ",
            "Phạt khi số lượng đồ ở một bước ít hơn bước trước. Người chịu trách nhiệm là người giữ đồ ở chặng trước (VD xưởng đếm thiếu so với lúc nhận → phạt tài xế lấy hàng). Mất nhiều thì tạo thêm phiếu tay.",
          )}
        </div>
      </div>

      {/* Card: Nợ đồ của khách — hạn trả + mức phạt khi để quá hạn */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Cấu hình nợ đồ của khách</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Đồ giặt chưa kịp, giao thiếu và hẹn trả ở đơn sau. Hệ thống quét đầu mỗi giờ, khoản nợ quá hạn mà
            chưa trả bù sẽ ra phiếu lỗi tự động, nâng dần tối đa 3 lần.
          </p>
        </div>

        <div className="px-5 divide-y divide-slate-100">
          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Thời hạn trả nợ đồ (giờ)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Tính từ lúc <b>đơn phát sinh nợ hoàn tất</b>. VD: 48 — quá 48h mà chưa giao bù ở đơn sau của
                khách thì phạt. Đặt <b>0</b> để tắt hẳn cơ chế phạt này.
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min="0"
                value={itemDebtDueHours}
                onChange={(e) => setItemDebtDueHours(e.target.value)}
                placeholder="VD: 48"
                className={NUM_INPUT}
              />
            </div>
          </div>

          {penaltyPointRow(
            FAULT_TYPE.NO_DO_QUA_HAN,
            "Nợ đồ quá hạn",
            "Phạt khi khoản nợ đồ của khách quá thời hạn ở trên mà chưa trả bù. Dùng chung cho cả 3 lần phạt; mỗi người bị phạt nhận 1 phiếu mỗi lần.",
          )}

          <ItemDebtPenaltyEditor
            value={itemDebtPenalty}
            departments={departments}
            onChange={setItemDebtPenalty}
            reloadKey={itemDebtPenaltyReloadKey}
          />
        </div>
      </div>

      {/* Card: Vị trí hoàn thành giao hàng — khoảng cách hợp lệ + điểm phạt */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Vị trí hoàn thành giao hàng</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Khi NV bấm hoàn thành giao trên app, hệ thống đối chiếu vị trí với địa chỉ khách. Vượt khoảng cách cho
            phép sẽ báo giám sát và tạo phiếu lỗi cho người bấm hoàn thành (mỗi đơn tối đa 1 phiếu).
          </p>
        </div>
        <div className="px-5 divide-y divide-slate-100">
          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Khoảng cách hợp lệ (mét)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Vị trí lúc bấm hoàn thành cách địa chỉ khách quá số mét này thì coi là sai vị trí. Để trống = <b>1000</b>.
                App cũng cảnh báo NV theo đúng ngưỡng này.
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min="1"
                value={deliveryLocationMaxM}
                onChange={(e) => setDeliveryLocationMaxM(e.target.value)}
                placeholder="1000"
                className={NUM_INPUT}
              />
            </div>
          </div>

          {penaltyPointRow(
            FAULT_TYPE.SAI_VI_TRI_GIAO,
            "Hoàn thành sai vị trí",
            "Để 0 = không tạo phiếu (chỉ cảnh báo giám sát). Khi có điểm, app bắt buộc bật định vị mới cho xác nhận giao. Khách chưa ghim toạ độ thì khoảng cách là ước lượng theo địa chỉ — NV giải trình nếu sai.",
          )}
        </div>
      </div>

      {/* Card 2: Cấu hình điều phối tài xế — ghép setting thời gian với mức phạt tương ứng */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Cấu hình điều phối tài xế</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Thời gian chờ và mức phạt (điểm) khi tài xế/giám sát không thao tác kịp.
          </p>
        </div>
        <div className="px-5 divide-y divide-slate-100">
          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Thời gian chờ tài xế xác nhận (phút)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Thời gian chờ tài xế xác nhận nhận/giao đơn. Quá thời gian sẽ tự động hủy và gán cho tài xế khác. Áp dụng cho cả nhận hàng và giao hàng.
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min="1"
                value={acceptTimeout}
                onChange={(e) => setAcceptTimeout(e.target.value)}
                placeholder="VD: 15"
                className={NUM_INPUT}
              />
            </div>
          </div>

          {penaltyPointRow(
            FAULT_TYPE.QUA_HAN_XAC_NHAN,
            "Quá hạn xác nhận đơn",
            "Phạt tài xế không xác nhận nhận/giao đơn trong thời gian chờ ở trên.",
          )}

          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Tần suất nhắc nhở tài xế (phút)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Khoảng cách giữa các lần gửi thông báo nhắc lại cho tài xế khi đơn chưa có ai nhận. Áp dụng cho cả nhận hàng và giao hàng.
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min="1"
                value={reminderInterval}
                onChange={(e) => setReminderInterval(e.target.value)}
                placeholder="VD: 1"
                className={NUM_INPUT}
              />
            </div>
          </div>

          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Thời gian giám sát điều phối đơn (phút)</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Quá thời gian này mà đơn vẫn chưa có ai nhận, hệ thống tạo phiếu lỗi "Không điều phối đơn" cho giám sát.
              </div>
            </div>
            <div className="col-span-3">
              <input
                type="number"
                min="0"
                value={dispatchMinutes}
                onChange={(e) => setDispatchMinutes(e.target.value)}
                placeholder="VD: 30"
                className={NUM_INPUT}
              />
            </div>
          </div>

          {penaltyPointRow(
            FAULT_TYPE.KHONG_DIEU_PHOI,
            "Không điều phối đơn (giám sát)",
            "Phạt giám sát/quản lý khi quá thời gian điều phối ở trên mà vẫn không có ai nhận đơn.",
          )}
        </div>
      </div>

      {/* Card 3: Mốc phạt tự động theo SLA (bảng) */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Mốc phạt tự động theo tiến độ đơn (SLA)</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Với mỗi mốc: đặt thời gian tối đa, rồi gán bộ phận chịu trách nhiệm + số điểm cho từng lần leo thang.
            Bộ phận không có nhân sự ở chi nhánh của đơn sẽ được bỏ qua.
          </p>
        </div>
        <div className="px-5 py-4">
          <FaultSlaEditor value={slaConfig} departments={departments} onChange={setSlaConfig} />
        </div>
      </div>

      {/* Card 4: Đồng bộ phiếu lỗi sang hệ thống đối tác */}
      <div className={CARD}>
        <div className={CARD_HEAD}>
          <h2 className={CARD_TITLE}>Đồng bộ phiếu lỗi sang hệ thống đối tác</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Gửi các phiếu <b>đã ghi lỗi</b> sang hệ thống đối tác để tính vào bảng lương bên đó. Chỉ phiếu đã
            chốt mới được gửi, và mỗi phiếu chỉ gửi một lần.
          </p>
        </div>

        <div className="px-5 divide-y divide-slate-100">
          <div className={ROW}>
            <div className="col-span-7">
              <div className="text-sm font-medium text-slate-700">Tự động gửi hằng ngày</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Bật thì hệ thống tự gửi lúc <b>01:00</b> mỗi ngày, quét các phiếu chốt trong <b>5 ngày</b> gần
                nhất. Tắt thì vẫn gửi tay được bằng nút <b>Đồng bộ sang đối tác</b> ở màn Quản lý phiếu lỗi.
              </div>
            </div>
            <div className="col-span-3 flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={partnerSyncEnabled}
                  onChange={(e) => setPartnerSyncEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-green-600 peer-focus:ring-2 peer-focus:ring-green-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white" />
                <span className="ml-3 text-sm text-slate-600">
                  {partnerSyncEnabled ? "Đang bật" : "Đã tắt"}
                </span>
              </label>
            </div>
          </div>

          <div className={ROW}>
            <div className="col-span-5">
              <div className="text-sm font-medium text-slate-700">Địa chỉ API của đối tác</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Đường dẫn đầy đủ đối tác cung cấp. Để trống thì không gửi được, kể cả gửi tay.
              </div>
            </div>
            <div className="col-span-5">
              <input
                type="text"
                value={partnerSyncUrl}
                onChange={(e) => setPartnerSyncUrl(e.target.value)}
                placeholder="https://.../api/aiGatePartner/receiveEmployeeFault"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
              />
            </div>
          </div>

          <div className={ROW}>
            <div className="col-span-5">
              <div className="text-sm font-medium text-slate-700">Secret key</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Khoá bí mật do đối tác cấp, dùng để ký từng request. Sai khoá thì đối tác từ chối toàn bộ lô.
              </div>
            </div>
            <div className="col-span-5">
              <input
                type="password"
                autoComplete="new-password"
                value={partnerSyncSecret}
                onChange={(e) => setPartnerSyncSecret(e.target.value)}
                placeholder="Khoá do đối tác cung cấp"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Thanh lưu dính đáy */}
      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-gradient-to-t from-white via-white to-transparent flex justify-end">
        <button
          disabled={!loaded || saving}
          onClick={handleSave}
          className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 shadow-sm"
        >
          {saving ? "Đang lưu..." : "Lưu cấu hình"}
        </button>
      </div>
    </div>
  );
};

export default SettingPage;
